/**
 * Static carrier registry for the Kayn platform.
 * All 18 supported carriers + their credential field definitions.
 */

export interface CarrierCredentialField {
  key: string;
  labelKey: string;
  type: "text" | "email" | "password";
}

interface PlatformCarrier {
  code: string;
  nameKey: string;
  category: "tier1" | "logestechs" | "rest" | "token" | "oauth" | "special";
  /** Brand color used for avatar background */
  brandColor: string;
  /** Short initials (1-3 chars) for avatar display */
  initials: string;
  credentialFields: CarrierCredentialField[];
}

// Reusable field definitions
const f = {
  apiKey: (carrier: string): CarrierCredentialField => ({
    key: "apiKey",
    labelKey: `shippingSettings.${carrier}Fields.apiKey`,
    type: "password",
  }),
  email: (carrier: string): CarrierCredentialField => ({
    key: "email",
    labelKey: `shippingSettings.${carrier}Fields.email`,
    type: "email",
  }),
  password: (carrier: string): CarrierCredentialField => ({
    key: "password",
    labelKey: `shippingSettings.${carrier}Fields.password`,
    type: "password",
  }),
  username: (carrier: string): CarrierCredentialField => ({
    key: "username",
    labelKey: `shippingSettings.${carrier}Fields.username`,
    type: "text",
  }),
  clientId: (carrier: string): CarrierCredentialField => ({
    key: "clientId",
    labelKey: `shippingSettings.${carrier}Fields.clientId`,
    type: "text",
  }),
  clientSecret: (carrier: string): CarrierCredentialField => ({
    key: "clientSecret",
    labelKey: `shippingSettings.${carrier}Fields.clientSecret`,
    type: "password",
  }),
  customerCode: (carrier: string): CarrierCredentialField => ({
    key: "customerCode",
    labelKey: `shippingSettings.${carrier}Fields.customerCode`,
    type: "text",
  }),
};

export const PLATFORM_CARRIERS: PlatformCarrier[] = [
  // Tier 1 (Sprint 8A/8E)
  {
    code: "aramex",
    nameKey: "shippingSettings.carrierNames.aramex",
    category: "tier1",
    brandColor: "#E35205",
    initials: "AX",
    credentialFields: [
      { key: "accountNumber", labelKey: "shippingSettings.aramexFields.accountNumber", type: "text" },
      { key: "email", labelKey: "shippingSettings.aramexFields.email", type: "email" },
      { key: "password", labelKey: "shippingSettings.aramexFields.password", type: "password" },
      { key: "accountPin", labelKey: "shippingSettings.aramexFields.accountPin", type: "text" },
      { key: "accountEntity", labelKey: "shippingSettings.aramexFields.accountEntity", type: "text" },
      { key: "accountCountryCode", labelKey: "shippingSettings.aramexFields.accountCountryCode", type: "text" },
    ],
  },
  {
    code: "smsa",
    nameKey: "shippingSettings.carrierNames.smsa",
    category: "tier1",
    brandColor: "#003B7B",
    initials: "SM",
    credentialFields: [
      { key: "passkey", labelKey: "shippingSettings.smsaFields.passkey", type: "password" },
    ],
  },
  {
    code: "aymakan",
    nameKey: "shippingSettings.carrierNames.aymakan",
    category: "tier1",
    brandColor: "#1A4B8C",
    initials: "AY",
    credentialFields: [
      f.apiKey("aymakan"),
    ],
  },
  {
    code: "naqel",
    nameKey: "shippingSettings.carrierNames.naqel",
    category: "tier1",
    brandColor: "#C41230",
    initials: "NQ",
    credentialFields: [
      f.clientId("naqel"),
      f.password("naqel"),
    ],
  },
  {
    code: "barq",
    nameKey: "shippingSettings.carrierNames.barq",
    category: "tier1",
    brandColor: "#6C3CFF",
    initials: "BQ",
    credentialFields: [
      f.email("barq"),
      f.password("barq"),
    ],
  },
  // Logestechs (Sprint 8F) — all share { email, password }
  {
    code: "yamamah",
    nameKey: "shippingSettings.carrierNames.yamamah",
    category: "logestechs",
    brandColor: "#2E7D32",
    initials: "YM",
    credentialFields: [f.email("yamamah"), f.password("yamamah")],
  },
  {
    code: "artz",
    nameKey: "shippingSettings.carrierNames.artz",
    category: "logestechs",
    brandColor: "#0D47A1",
    initials: "AZ",
    credentialFields: [f.email("artz"), f.password("artz")],
  },
  {
    code: "receivehere",
    nameKey: "shippingSettings.carrierNames.receivehere",
    category: "logestechs",
    brandColor: "#00897B",
    initials: "RH",
    credentialFields: [f.email("receivehere"), f.password("receivehere")],
  },
  {
    code: "fourship",
    nameKey: "shippingSettings.carrierNames.fourship",
    category: "logestechs",
    brandColor: "#F57C00",
    initials: "4S",
    credentialFields: [f.email("fourship"), f.password("fourship")],
  },
  // REST (Sprint 8F)
  {
    code: "jt",
    nameKey: "shippingSettings.carrierNames.jt",
    category: "rest",
    brandColor: "#D32F2F",
    initials: "JT",
    credentialFields: [
      f.apiKey("jt"),
      f.customerCode("jt"),
    ],
  },
  {
    code: "fastlo",
    nameKey: "shippingSettings.carrierNames.fastlo",
    category: "rest",
    brandColor: "#1565C0",
    initials: "FL",
    credentialFields: [f.apiKey("fastlo")],
  },
  {
    code: "thabit",
    nameKey: "shippingSettings.carrierNames.thabit",
    category: "rest",
    brandColor: "#4A148C",
    initials: "TH",
    credentialFields: [f.apiKey("thabit")],
  },
  {
    code: "zajel",
    nameKey: "shippingSettings.carrierNames.zajel",
    category: "rest",
    brandColor: "#00695C",
    initials: "ZJ",
    credentialFields: [
      f.apiKey("zajel"),
      f.customerCode("zajel"),
      { key: "serviceTypeId", labelKey: "shippingSettings.zajelFields.serviceTypeId", type: "text" },
    ],
  },
  // Token-auth (Sprint 8F)
  {
    code: "quick",
    nameKey: "shippingSettings.carrierNames.quick",
    category: "token",
    brandColor: "#FF6F00",
    initials: "QK",
    credentialFields: [f.username("quick"), f.password("quick")],
  },
  {
    code: "clex",
    nameKey: "shippingSettings.carrierNames.clex",
    category: "token",
    brandColor: "#283593",
    initials: "CX",
    credentialFields: [f.username("clex"), f.password("clex")],
  },
  // OAuth/Special (Sprint 8F)
  {
    code: "lamabox",
    nameKey: "shippingSettings.carrierNames.lamabox",
    category: "oauth",
    brandColor: "#5C6BC0",
    initials: "LB",
    credentialFields: [f.clientId("lamabox"), f.clientSecret("lamabox")],
  },
  {
    code: "redbox",
    nameKey: "shippingSettings.carrierNames.redbox",
    category: "special",
    brandColor: "#B71C1C",
    initials: "RB",
    credentialFields: [f.apiKey("redbox")],
  },
  {
    code: "shipox",
    nameKey: "shippingSettings.carrierNames.shipox",
    category: "special",
    brandColor: "#37474F",
    initials: "SX",
    credentialFields: [
      f.username("shipox"),
      f.password("shipox"),
      { key: "instance", labelKey: "shippingSettings.shipoxFields.instance", type: "text" },
    ],
  },
];

/** Look up a carrier by code */
export function getCarrier(code: string): PlatformCarrier | undefined {
  return PLATFORM_CARRIERS.find((c) => c.code === code);
}

/** Get the logo URL for a carrier code (served from /carriers/{code}.svg) */
export function getCarrierLogoUrl(code: string): string {
  return `/carriers/${code}.svg`;
}
